import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, CheckCircle, Clock, XCircle, Send, MessageSquare, AlertTriangle, Calendar, Tag, Activity, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { GeminiHeader } from '@/components/GeminiHeader';
import { RiskRatingBadge } from '@/components/RiskRatingBadge';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface ReportData {
  id: string;
  location: string;
  description: string;
  hazard_category: string;
  hazard_classification: string;
  risk_rating: string;
  risk_score: number;
  verification_status: string;
  photo_url: string | null;
  created_at: string;
  xp_awarded: number;
  supervisor_notes: string | null;
  clarification_response: string | null;
  clarification_responded_at: string | null;
  work_activity: string | null;
  cause_category: string | null;
  cause_details: string | null;
}

export default function ReportDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<ReportData | null>(null);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [clarificationText, setClarificationText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadReport();
  }, [id]);

  const loadReport = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        navigate('/signup');
        return;
      }

      const { data: profile } = await supabase
        .from('worker_profiles')
        .select('*')
        .eq('user_id', authUser.id)
        .maybeSingle();

      if (!profile) {
        navigate('/signup');
        return;
      }

      setUser({
        id: profile.id,
        displayName: profile.display_name,
        role: profile.role,
        team: profile.team,
        xp: profile.total_xp || 0,
        streak: profile.consecutive_safe_days || 0,
        badges: [],
        psychometricVector: [],
        baselineRisk: profile.baseline_risk_score,
        currentRisk: profile.current_risk_score,
        anonymousMode: false,
        createdAt: new Date(profile.created_at),
      });

      const { data, error } = await supabase
        .from('peer_reports')
        .select('*')
        .eq('id', id)
        .eq('reporter_id', profile.id)
        .single();

      if (error) throw error;
      setReport(data);
    } catch (error) {
      console.error('Error loading report:', error);
      toast.error('Gagal memuat laporan');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitClarification = async () => {
    if (!clarificationText.trim() || !report) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('peer_reports')
        .update({
          clarification_response: clarificationText,
          clarification_responded_at: new Date().toISOString(),
          verification_status: 'clarification_submitted',
        })
        .eq('id', report.id);

      if (error) throw error;

      const { data: userProfile } = await supabase
        .from('worker_profiles')
        .select('site_id, company_id')
        .eq('id', user.id)
        .single();

      if (userProfile?.site_id) {
        const { data: supervisors } = await supabase
          .from('worker_profiles')
          .select('id')
          .eq('role', 'supervisor')
          .eq('site_id', userProfile.site_id)
          .limit(5);

        if (supervisors && supervisors.length > 0) {
          const notifications = supervisors.map(sup => ({
            recipient_id: sup.id,
            notification_type: 'clarification_received',
            title: 'Klarifikasi Diterima',
            message: `Pekerja telah memberikan klarifikasi untuk laporan di ${report.location}.`,
            is_anonymous: false,
            data: { report_id: report.id, has_clarification: true }
          }));

          await supabase.from('notifications').insert(notifications);
        }
      }

      toast.success('Klarifikasi berhasil dikirim');
      setReport(prev => prev ? {
        ...prev,
        clarification_response: clarificationText,
        clarification_responded_at: new Date().toISOString(),
        verification_status: 'clarification_submitted'
      } : null);
      setClarificationText('');
    } catch (error) {
      console.error('Error submitting clarification:', error);
      toast.error('Gagal mengirim klarifikasi');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { label: string; color: string; bgColor: string }> = {
      'verified': { label: 'Dikonfirmasi', color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-50 dark:bg-emerald-950/30' },
      'hazard_confirmed': { label: 'Dikonfirmasi', color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-50 dark:bg-emerald-950/30' },
      'pending_verification': { label: 'Menunggu Review', color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-50 dark:bg-amber-950/30' },
      'needs_clarification': { label: 'Butuh Info', color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-50 dark:bg-amber-950/30' },
      'clarification_submitted': { label: 'Klarifikasi Terkirim', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-50 dark:bg-blue-950/30' },
      'rejected': { label: 'Ditolak', color: 'text-rose-600 dark:text-rose-400', bgColor: 'bg-rose-50 dark:bg-rose-950/30' },
    };
    return configs[status] || configs['pending_verification'];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!report || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground text-sm">Laporan tidak ditemukan</p>
      </div>
    );
  }

  const statusConfig = getStatusConfig(report.verification_status);
  const needsClarification = report.verification_status === 'needs_clarification';

  return (
    <div className="min-h-screen bg-background pb-24">
      <GeminiHeader 
        title="Detail Laporan" 
        subtitle={report.location}
        backPath="/history"
        rightElement={<RiskRatingBadge rating={report.risk_rating as any} score={report.risk_score} showScore={false} />}
      />

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Status Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-2xl p-4 mb-6 shadow-md shadow-black/5 dark:shadow-black/20 ${statusConfig.bgColor}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`h-3 w-3 rounded-full ${statusConfig.color.replace('text-', 'bg-').replace('-600', '-500').replace('-400', '-500')}`} />
              <span className={`font-medium ${statusConfig.color}`}>{statusConfig.label}</span>
            </div>
            {report.xp_awarded > 0 && (
              <span className="text-sm font-semibold text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-2.5 py-1 rounded-lg">
                +{report.xp_awarded} XP
              </span>
            )}
          </div>
        </motion.div>

        {/* Photo */}
        {report.photo_url && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mb-6 rounded-2xl overflow-hidden shadow-lg shadow-black/10 dark:shadow-black/30"
          >
            <img 
              src={report.photo_url} 
              alt="Bukti foto" 
              className="w-full h-56 object-cover"
            />
          </motion.div>
        )}

        {/* Description Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-2xl bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-950/30 dark:to-gray-950/30 p-5 mb-6 shadow-md shadow-black/5 dark:shadow-black/20"
        >
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Deskripsi</span>
          </div>
          <p className="text-base leading-relaxed">{report.description}</p>
        </motion.div>

        {/* Details Grid */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="grid grid-cols-2 gap-3 mb-6"
        >
          <div className="rounded-2xl bg-muted/50 dark:bg-muted/30 p-4 shadow-md shadow-black/5 dark:shadow-black/20">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Lokasi</span>
            </div>
            <p className="font-medium text-sm">{report.location}</p>
          </div>

          <div className="rounded-2xl bg-muted/50 dark:bg-muted/30 p-4 shadow-md shadow-black/5 dark:shadow-black/20">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Tanggal</span>
            </div>
            <p className="font-medium text-sm">
              {format(new Date(report.created_at), 'dd MMM yyyy')}
            </p>
          </div>

          <div className="rounded-2xl bg-muted/50 dark:bg-muted/30 p-4 shadow-md shadow-black/5 dark:shadow-black/20">
            <div className="flex items-center gap-2 mb-2">
              <Tag className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Kategori</span>
            </div>
            <p className="font-medium text-sm capitalize">
              {report.hazard_category?.replace(/_/g, ' ')}
            </p>
          </div>

          {report.work_activity && (
            <div className="rounded-2xl bg-muted/50 dark:bg-muted/30 p-4 shadow-md shadow-black/5 dark:shadow-black/20">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Aktivitas</span>
              </div>
              <p className="font-medium text-sm capitalize">
                {report.work_activity}
              </p>
            </div>
          )}
        </motion.div>

        {/* Cause Details */}
        {(report.cause_category || report.cause_details) && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="rounded-2xl bg-muted/40 p-4 mb-6 shadow-sm"
          >
            <h3 className="text-sm font-medium text-muted-foreground mb-3">Analisis Penyebab</h3>
            {report.cause_category && (
              <div className="flex items-center justify-between py-2 border-b border-border/50">
                <span className="text-sm text-muted-foreground">Kategori</span>
                <span className="text-sm font-medium capitalize">{report.cause_category.replace(/_/g, ' ')}</span>
              </div>
            )}
            {report.cause_details && (
              <div className="pt-3">
                <span className="text-sm text-muted-foreground block mb-1">Detail</span>
                <p className="text-sm">{report.cause_details}</p>
              </div>
            )}
          </motion.div>
        )}

        {/* Supervisor Notes */}
        {report.supervisor_notes && !needsClarification && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mb-6 p-4 rounded-2xl bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-950/30 dark:to-gray-950/30 shadow-md shadow-black/5 dark:shadow-black/20"
          >
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">Catatan Supervisor</span>
            </div>
            <p className="text-sm">{report.supervisor_notes}</p>
          </motion.div>
        )}

        {/* Clarification Needed */}
        {needsClarification && !report.clarification_response && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mb-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-medium">Info Tambahan Diperlukan</span>
            </div>
            
            {report.supervisor_notes && (
              <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 mb-4 shadow-md shadow-amber-500/5">
                <div className="text-xs text-amber-600 dark:text-amber-400 mb-1">Pertanyaan Supervisor:</div>
                <p className="text-sm">{report.supervisor_notes}</p>
              </div>
            )}
            
            <Textarea
              value={clarificationText}
              onChange={(e) => setClarificationText(e.target.value)}
              placeholder="Tulis jawaban Anda..."
              rows={4}
              className="mb-4 rounded-xl border-border/50 bg-muted/30 focus:bg-background transition-colors"
            />
            <Button
              onClick={handleSubmitClarification}
              disabled={!clarificationText.trim() || submitting}
              className="w-full rounded-xl h-12 shadow-md"
            >
              {submitting ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Kirim Klarifikasi
                </>
              )}
            </Button>
          </motion.div>
        )}

        {/* Previous Clarification */}
        {report.clarification_response && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-6 p-4 rounded-2xl bg-muted/50 dark:bg-muted/30 shadow-md shadow-black/5 dark:shadow-black/20"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-primary" />
                <span className="text-xs font-medium">Klarifikasi Terkirim</span>
              </div>
              {report.clarification_responded_at && (
                <span className="text-xs text-muted-foreground">
                  {format(new Date(report.clarification_responded_at), 'dd MMM, HH:mm')}
                </span>
              )}
            </div>
            <p className="text-sm">{report.clarification_response}</p>
          </motion.div>
        )}

        {/* Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="rounded-2xl bg-muted/40 p-4 shadow-sm"
        >
          <h3 className="text-sm font-medium text-muted-foreground mb-4">Timeline</h3>
          <div className="relative pl-4 border-l-2 border-border/50 space-y-4">
            <div className="relative">
              <div className="absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-4 ring-background" />
              <div>
                <div className="text-sm font-medium">Laporan Dibuat</div>
                <div className="text-xs text-muted-foreground">{format(new Date(report.created_at), 'dd MMM yyyy, HH:mm')}</div>
              </div>
            </div>
            
            {report.clarification_responded_at && (
              <div className="relative">
                <div className="absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full bg-blue-500 ring-4 ring-background" />
                <div>
                  <div className="text-sm font-medium">Klarifikasi Dikirim</div>
                  <div className="text-xs text-muted-foreground">{format(new Date(report.clarification_responded_at), 'dd MMM yyyy, HH:mm')}</div>
                </div>
              </div>
            )}
            
            {(report.verification_status === 'verified' || report.verification_status === 'hazard_confirmed') && (
              <div className="relative">
                <div className="absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-4 ring-background" />
                <div>
                  <div className="text-sm font-medium">Dikonfirmasi</div>
                  <div className="text-xs text-muted-foreground">Hazard terverifikasi</div>
                </div>
              </div>
            )}
            
            {report.verification_status === 'rejected' && (
              <div className="relative">
                <div className="absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full bg-rose-500 ring-4 ring-background" />
                <div>
                  <div className="text-sm font-medium">Ditolak</div>
                  <div className="text-xs text-muted-foreground">Laporan tidak diterima</div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}